import { allocateCents, type Cents } from '@keel/core';

/** Editable split row (no id until saved). `percent` is set when the row was allocated by percentage. */
export interface SplitDraft {
  readonly key: string;
  readonly categoryId: string | null;
  readonly amountCents: Cents;
  readonly memo: string;
  readonly tagIds: readonly string[];
  readonly percent?: number | null;
}

/** Cents still to allocate: total − Σ splits (0 when balanced). */
export const splitRemainder = (totalCents: Cents, splits: readonly Pick<SplitDraft, 'amountCents'>[]): Cents =>
  totalCents - splits.reduce((s, x) => s + x.amountCents, 0);

/**
 * Re-allocate `total` across splits by their percentages with core `allocateCents` (largest remainder, so the parts
 * always sum to the total exactly). Rows without a percent keep their amount; the percent rows share what's left.
 */
export const allocateByPercent = <S extends SplitDraft>(totalCents: Cents, splits: readonly S[]): S[] => {
  const fixed = splits.filter((s) => s.percent == null).reduce((a, s) => a + s.amountCents, 0);
  const pctRows = splits.filter((s) => s.percent != null);
  if (!pctRows.length) return [...splits];
  const pctTotal = pctRows.reduce((a, s) => a + (s.percent ?? 0), 0);
  // Percent rows cover pctTotal % of the total; if they add to 100 % they absorb everything not fixed.
  const pool = pctTotal >= 99.995 ? totalCents - fixed : Math.round((totalCents * pctTotal) / 100);
  const parts = allocateCents(pool, pctRows.map((s) => s.percent ?? 0));
  let i = 0;
  return splits.map((s) => (s.percent == null ? s : { ...s, amountCents: parts[i++] ?? 0 }));
};

/** Put the remainder on the last split so the transaction balances. */
export const absorbRemainder = <S extends SplitDraft>(totalCents: Cents, splits: readonly S[]): S[] => {
  const rem = splitRemainder(totalCents, splits);
  if (!rem || !splits.length) return [...splits];
  const last = splits[splits.length - 1] as S;
  return [...splits.slice(0, -1), { ...last, amountCents: last.amountCents + rem, percent: null }];
};
